import {FlatList, View} from 'native-base';
import React from 'react';

import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {OfferType, OfferTypeAll} from 'models/Offer';

import OfferTypeItem from './OfferTypeItem';

export interface OfferTypesFilterSelectorProps {
  selectedOfferTypes: OfferType[];
  setSelectedOfferTypes: (offerTypes: OfferType[]) => void;
  availableOfferTypes: OfferType[];
}

const OfferTypesFilterSelector: React.FC<
  OfferTypesFilterSelectorProps
> = props => {
  const {selectedOfferTypes, setSelectedOfferTypes, availableOfferTypes} =
    props;
  const hasOneAvailableType = availableOfferTypes.length === 1;
  const offerTypesFilterList = [OfferTypeAll, ...Object.values(OfferType)];

  const {trackAdobeEvent} = useTrackEvent();

  const isAllOfferTypesSelected = availableOfferTypes.every(a =>
    selectedOfferTypes.find(s => a === s)
  );

  const isOfferTypeDisabled = offerType =>
    !availableOfferTypes.includes(offerType) && offerType !== OfferTypeAll;

  const isOfferTypeSelected = offerType => {
    if (hasOneAvailableType && selectedOfferTypes.length === 1) {
      return selectedOfferTypes.includes(offerType);
    }
    return isAllOfferTypesSelected
      ? offerType === OfferTypeAll
      : selectedOfferTypes.includes(offerType);
  };

  const onOfferTypePress = offerType => {
    const isSelected = isOfferTypeSelected(offerType);
    const isDisabled = isOfferTypeDisabled(offerType);

    if (!isSelected && isDisabled) {
      return;
    }
    let newOfferTypes: OfferType[] = [];

    if (offerType === OfferTypeAll) {
      newOfferTypes = [...availableOfferTypes];
    }
    if (offerType !== OfferTypeAll && isAllOfferTypesSelected) {
      newOfferTypes = [offerType];
    }
    if (isSelected) {
      // If only one item is left, revert to 'All' (PROF-2588)
      if (selectedOfferTypes.length === 1) {
        newOfferTypes = [...availableOfferTypes];
      }
      newOfferTypes = selectedOfferTypes.filter(type => type !== offerType);
    }
    if (selectedOfferTypes.length + 1 === availableOfferTypes.length) {
      newOfferTypes = [...availableOfferTypes];
    }

    if (newOfferTypes.length === 0) {
      newOfferTypes = [...selectedOfferTypes, offerType];
    }
    setSelectedOfferTypes(newOfferTypes);
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {name: 'rewards filter'},
      rewards: {category: 'OFFER TYPES', filter: newOfferTypes},
    });
  };

  const renderItem = ({item}) => {
    const isSelected = isOfferTypeSelected(item);
    const isDisabled = isOfferTypeDisabled(item);
    return (
      <OfferTypeItem
        offerType={item}
        isSelected={isSelected}
        isDisabled={!isSelected && isDisabled}
        onPress={onOfferTypePress}
      />
    );
  };

  return (
    <View testID="offer-type-filter-selector" p="14px">
      <FlatList
        data={offerTypesFilterList}
        renderItem={renderItem}
        numColumns={3}
        extraData={selectedOfferTypes}
      />
    </View>
  );
};

export default OfferTypesFilterSelector;
